import { thirdLoginOK } from "@/components/LoginModal/shared/third";
import { EVENT } from "@/constants/event";
import { PIN_OPEN_TYPE } from "@/constants/pinOpenType";
import {
  TRANSPARENT_IMG_BG,
  TRANSPARENT_IMG_BG_LIST,
} from "@/constants/transparentImgBg";
import { isBigCompanyOrder } from "@/constants/vip";
import { getBizOrderList, getCategories, getGrocery } from "@/services";
import loginTracker from "@/tracker/report/loginTracker";
import { isProd } from "@/utils/env";
import getScrollbarWidth from "@/utils/getScrollbarWidth";
import onlyVisibleRun from "@/utils/onlyVisibleRun";
import { safeParse } from "@/utils/safeParse";
import { storeSetHookMiddire } from "@/utils/storeSetHook";
import useRequest from "@/utils/useReq";
import { useMount, useUpdateEffect } from "ahooks";
import Cookies from "js-cookie";
import { once, pick } from "lodash";
import { useEffect } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import app from "./modules/app";
import board from "./modules/board";
import dialog from "./modules/dialog";
import style from "./modules/style";
import user from "./modules/user";
import vip from "./modules/vip";
import { useTabShare } from "./share";
/**
 *  @template T
 *  @typedef {()=>T} creator
 */

// 聚合所有mod
/**
 * @template T1
 * @template T2
 * @template T3
 * @template T4
 * @template T5
 * @template T6
 * @param { [creator<T1>, creator<T2>, creator<T3>, creator<T4>, creator<T5>, creator<T6>] } moduleArr
 * @returns {(set: any)=>{setStore: (param: Partial<T1&T2&T3&T4&T5&T6>)=>void}&T1&T2&T3&T4&T5&T6}
 */
function aggregateStore(moduleArr) {
  return (set, get, api) => {
    return Object.assign(
      { setStore: set },
      ...moduleArr.map((createStoreMod) => createStoreMod(set, get, api))
    );
  };
}

const useAppStore = create(
  devtools(
    storeSetHookMiddire(aggregateStore([user, vip, style, app, board, dialog])),
    {
      enabled: !isProd(),
    }
  )
);

export default useAppStore;

const clientInitStore = once((serverSideStore) => {
  useAppStore.setState(serverSideStore);
});

const initStore = (serverSideStore) => {
  if (typeof window === "undefined") {
    // 服务端每次都要初始化所有状态值到初始值，确保前后请求不会互相影响
    const initState = aggregateStore([user, vip, style, app, board])(
      useAppStore.setState,
      useAppStore.getState,
      useAppStore
    );
    useAppStore.setState({ ...initState, ...serverSideStore });
  } else {
    clientInitStore(serverSideStore);
  }
};

export function useInitStore(serverSideStore) {
  initStore(serverSideStore);
  const {
    user,
    setStore,
    refreshCouponNum,
    refreshDmUnread,
    refreshUnreadMsg,
    refreshVips,
    refreshLastBoards,
  } = useAppStore((store) =>
    pick(store, [
      "setStore",
      "user",
      "refreshLastBoards",
      "refreshCouponNum",
      "refreshDmUnread",
      "refreshUnreadMsg",
      "refreshUser",
      "refreshVips",
    ])
  );

  // let synchronousDataRef = useTabShare();
  // // 轮询获取最新消息开始的时间
  // let loopStartTime = useMemo(() => {
  //   // 距离同步过来的时间的5分钟
  //   return (
  //     5 * 60 * 1000 +
  //     (synchronousDataRef.current?.dmUnread?.time || Date.now()) -
  //     Date.now()
  //   );
  // }, [synchronousDataRef]);

  // // 判断是否从localStorage中同步过，如果同步过，则不做初始化请求操作
  // const hasSynchronous = (key) => {
  //   let data = synchronousDataRef.current || {};
  //   if (data[key]) {
  //     // 删除这个值，避免下次需要更新时而不更新（比如退出登录）
  //     synchronousDataRef.current = omit(data, [key]);
  //     return true;
  //   }
  //   return false;
  // };

  // 获取未支付订单信息，用于未支付提醒
  const getOrders = async () => {
    const result = await getBizOrderList(
      {
        status: 0,
      },
      true
    );
    if (result?.err) return;

    setStore({
      unPayOrders: result?.list
        ? result.list.filter((order) => {
            // 排除连续包月订单、大企业订单
            return (
              !isBigCompanyOrder(order) &&
              !order.skus.find((sku) => sku?.ref_body?.goods?.is_auto_renew)
            );
          })
        : [],
    });
  };

  const { getTabShareData, getShareUpdateTime } = useTabShare();

  // 所有第一次加载需要初始化请求的数据， 依赖用户登录的，且根据用户id进行变化的
  useMount(async () => {
    if (user) {
      // 从其他tab页共享的数据
      const shareStore = await getTabShareData();

      // 判断是否有 couponNum 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("couponNum")) {
        refreshCouponNum();
      }

      // 判断是否有 dmUnread 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("dmUnread")) {
        refreshDmUnread();
      }

      // 判断是否有 unreadNum 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("unreadNum")) {
        refreshUnreadMsg();
      }

      // 判断是否有 lastBoards 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("lastBoards")) {
        refreshLastBoards();
      }

      // 判断是否有 unPayOrders 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("unPayOrders")) {
        getOrders();
      }

      // 判断是否有 vip 属性，如果没有，则需要初始化请求
      if (!shareStore.hasOwnProperty("vip")) {
        refreshVips();
      }

      // 初始化获取用户信息
      setStore(shareStore, false, true);
    } else {
      // 未登录时，清空数据
      setStore({
        unreadNum: 0,
        dmUnread: 0,
        couponNum: 0,
        lastBoards: [],
        unPayOrders: [],
        expiredVips: null,
        vips: [],
      });
    }
  });

  useUpdateEffect(() => {
    if (user) {
      // 优惠券数量
      refreshCouponNum();
      refreshDmUnread();
      refreshUnreadMsg();
      refreshLastBoards();
      getOrders();
      refreshVips();
    } else {
      // 未登录时，清空数据
      setStore({
        unreadNum: 0,
        dmUnread: 0,
        couponNum: 0,
        lastBoards: [],
        unPayOrders: [],
        expiredVips: null,
        vips: [],
      });
    }
  }, [user?.user_id]);

  const getUser = async () => {
    // 服务端已经获取过用户信息，不需要再次获取
    // const userResult = await refreshUser();
    // 第三方登录的不计入静默登录埋点

    if (user) {
      thirdLoginOK() ||
        loginTracker({
          userId: user.user_id,
          is_cache_login: true,
        });
    }
  };

  const getDomains = async () => {
    const domains = await getGrocery("domain-recommend");
    setStore({
      domains: domains?.list || [],
    });
  };

  const getCategory = async () => {
    const result = await getCategories();
    const list = result?.categories?.map((category) => ({
      ...category,
      value: category.id,
    }));

    setStore({
      categories: list || [],
    });
  };

  // 不依赖登录的初始化数据
  useRequest(async () => {
    // 初始化获取用户信息
    getUser();
    /** 杂货铺来源字典数据 */
    getDomains();
    // 分类信息
    getCategory();
  });

  // 定时刷新消息信息
  const { run: startLoop } = useRequest(
    onlyVisibleRun(async () => {
      if (user) {
        refreshDmUnread();
        refreshUnreadMsg();
        refreshCouponNum();
      }
    }),
    {
      refreshDeps: [user],
      // 5分钟刷新
      pollingInterval: 5 * 60 * 1000,
      // 手动开启时间
      manual: true,
    }
  );

  useMount(async () => {
    const updateTime = await getShareUpdateTime();
    // 计算轮询开始时间
    const loopStartTime =
      5 * 60 * 1000 + ((updateTime || Date.now()) - Date.now());

    setTimeout(startLoop, loopStartTime);
  });

  useEffect(() => {
    const scrollbarWidth = getScrollbarWidth();
    document.body.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
  }, []);

  // 宽度变化
  useEffect(() => {
    const onChange = () => {
      const width = Math.max(window.innerWidth, 1120);
      const paddingWidth = 42;
      const itemWidth = 240;
      const itemGap = 24;
      let maxNum = Math.floor(
        (width - paddingWidth * 2 + itemGap) / (itemWidth + itemGap)
      );
      let num = maxNum;
      let imageBackground = TRANSPARENT_IMG_BG.GRID_BG;
      let pinOpenType = PIN_OPEN_TYPE.MODAL;
      let columnType = "full";

      const pageSettingStr = window.localStorage.getItem("huaban-page-setting");
      if (pageSettingStr) {
        const pageSetting = safeParse(pageSettingStr);
        if (pageSetting?.columnType === "limit" && pageSetting?.limit < num) {
          num = pageSetting?.limit;
        }
        imageBackground =
          pageSetting?.imageBackground || TRANSPARENT_IMG_BG.GRID_BG;
        pinOpenType = pageSetting?.pinOpenType || PIN_OPEN_TYPE.MODAL;
        columnType = pageSetting?.columnType || "full";
      }

      TRANSPARENT_IMG_BG_LIST.forEach((item) => {
        document.body.classList.remove(`transparent-img-${item.value}`);
      });
      document.body.classList.add(`transparent-img-${imageBackground}`);
      document.body.setAttribute("data-column-limit", num);

      // 只存这两项给服务端使用
      Cookies.set("huaban-page-setting", {
        columnType: columnType,
        limit: num,
      });

      setStore({
        wrapperWidth: num * (itemWidth + itemGap) - itemGap,
        wrapperWidthReady: true,
        column: num,
        pinOpenType,
        maxWrapperWidth: maxNum * (itemWidth + itemGap) - itemGap,
      });
    };

    onChange();

    window.addEventListener("resize", onChange);
    window.addEventListener(EVENT.PAGE_SETTING_CHANGE, onChange);

    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener(EVENT.PAGE_SETTING_CHANGE, onChange);
    };
  }, [setStore]);
}
